import {
    Tree,
    TreeItem,
    TreeItemLayout,
    Text,
    SearchBox,
    Divider
} from "@fluentui/react-components";
import {
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
} from "@fluentui/react-components";
import { ChevronDown24Filled } from "@fluentui/react-icons";


import React, { useEffect } from "react";
import { OiReport } from "./OiReport";
import { getAllOiAnalysisOrdersGrouped, getAnalysisOrdersFiltered, OiAnalysisOrderGrouped } from "../../services/AnalysisOrderService";
import { getAllOiAnalysisResultsGrouped, getAnalysisResultsFiltered, OiAnalysisResultGrouped } from "../../services/AnalysisResultService";
import { GetPOIList, FilterPOIBy, GetFilteredPOICount } from "../../services/OiService";
import { getAllOiPowderOrdersGrouped, getPowderOrdersFiltered, OiPowderOrderGrouped } from "../../services/PowderOrderService";
import { getAllRawMaterialControlsGrouped, getRawMaterialControlsFiltered, RawMaterialControlGrouped } from "../../services/RawMaterialService";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import PaginatedTreeGroup, { PaginatedTreeItem } from "./PaginatedTreeGroup";

export interface OiTreeRef {
    refreshReports: () => Promise<void>;
}



const OI_TREE_STORAGE_KEY = "oiTreeExpandedItems";

export default function OiTree() {

    const newButtonContainerRef = React.useRef<HTMLDivElement | null>(null);
    const newButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const [isNewMenuOpen, setIsNewMenuOpen] = React.useState(false);
    const [newMenuWidth, setNewMenuWidth] = React.useState<number | undefined>(undefined);
    const [newMenuBg, setNewMenuBg] = React.useState<string | undefined>(undefined);

    const [allReports, setAllReports] = React.useState<OiReport[]>([]);
    const [allPowderOrders, setAllPowderOrders] = React.useState<OiPowderOrderGrouped | null>(null);
    const [allAnalysisOrders, setAllAnalysisOrders] = React.useState<OiAnalysisOrderGrouped | null>(null);
    const [allAnalysisResults, setAllAnalysisResults] = React.useState<OiAnalysisResultGrouped | null>(null);
    const [allRawMaterialControls, setAllRawMaterialControls] = React.useState<RawMaterialControlGrouped | null>(null);


    const [pages, setPages] = React.useState<Record<string, number>>({});
    const setPage = React.useCallback((groupKey: string, page: number) => {
        setPages(current => ({ ...current, [groupKey]: page }));
    }, []);
    const navigate = useNavigate();
    const location = useLocation();
    const [openItems, setOpenItems] = React.useState<Set<string>>(() => {
        try {
            const parsed = JSON.parse(window.localStorage.getItem(OI_TREE_STORAGE_KEY) ?? "[]");
            return Array.isArray(parsed) ? new Set<string>(parsed.map(String)) : new Set<string>();
        } catch {
            return new Set<string>();
        }
    });
    const treeInteractedRef = React.useRef(false);
    React.useEffect(() => {
        if (!treeInteractedRef.current) {
            treeInteractedRef.current = true;
            return;
        }
        window.localStorage.setItem(OI_TREE_STORAGE_KEY, JSON.stringify(Array.from(openItems)));
    }, [openItems]);
    const { key } = location

    const [searchQuery, setSearchQuery] = React.useState<string>("");
    const refreshTick = useAppSelector(state => state.oiTreeSlice.refreshTick);


    const pageSize = 20;
    const searchActive = searchQuery.trim().length > 0;

    const parsePowderOrderId = React.useCallback((orderId?: string) => {
        if (!orderId) {
            return null;
        }

        const match = orderId.match(/^(.*?)-(\d{6})-(\d+)$/);
        if (!match) {
            return null;
        }

        const [, prefix, rawDate, rawSequence] = match;
        const year = 2000 + Number(rawDate.slice(0, 2));
        const month = Number(rawDate.slice(2, 4));
        const day = Number(rawDate.slice(4, 6));
        const sequence = Number(rawSequence);
        const timestamp = new Date(year, month - 1, day).getTime();

        if (Number.isNaN(timestamp) || Number.isNaN(sequence)) {
            return null;
        }

        return {
            prefix,
            timestamp,
            sequence,
        };
    }, []);

    const comparePowderOrderIds = React.useCallback((left?: string, right?: string) => {
        const parsedLeft = parsePowderOrderId(left);
        const parsedRight = parsePowderOrderId(right);

        if (parsedLeft && parsedRight) {
            if (parsedLeft.timestamp !== parsedRight.timestamp) {
                return parsedRight.timestamp - parsedLeft.timestamp;
            }

            if (parsedLeft.sequence !== parsedRight.sequence) {
                return parsedRight.sequence - parsedLeft.sequence;
            }
        }

        return (right ?? "").localeCompare(left ?? "");
    }, [parsePowderOrderId]);

    const fetchAllReports = React.useCallback(async () => {
        try {
            if (searchQuery.trim().length >= 3) {
                const totalCount = await GetFilteredPOICount(searchQuery);
                if (totalCount !== null && totalCount > 0) {
                    const estimatedPagesNeeded = Math.ceil(totalCount / pageSize);
                    let allSearchResults: OiReport[] = [];
                    const pagesToFetch = Math.min(estimatedPagesNeeded, 5);
                    for (let i = 0; i < pagesToFetch; i++) {
                        const reportList = await FilterPOIBy(searchQuery, i, pageSize);
                        if (reportList && reportList.length > 0) {
                            allSearchResults.push(...reportList);
                        } else {
                            break;
                        }
                    }
                    setAllReports(allSearchResults);
                } else {
                    setAllReports([]);
                }
            } else {
                const reportList = await GetPOIList(0, 160);
                setAllReports(reportList || []);
            }
        } catch (error) {
            console.error('Error fetching reports or powder orders:', error);
            setAllReports([]);

        }
    }, [searchQuery]);

    useEffect(() => {
        const timeout = setTimeout(() => {

            const fetchData = async () => {
                try {
                    if (searchQuery.trim().length >= 3) {
                        const [analysisOrders, analysisResults, powderOrders, rawMaterialControls] = await Promise.all([
                            getAnalysisOrdersFiltered(searchQuery),
                            getAnalysisResultsFiltered(searchQuery),
                            getPowderOrdersFiltered(searchQuery),
                            getRawMaterialControlsFiltered(searchQuery),
                        ]);
                        setAllAnalysisOrders(analysisOrders);
                        setAllAnalysisResults(analysisResults);
                        setAllPowderOrders(powderOrders);
                        setAllRawMaterialControls(rawMaterialControls);
                    } else {
                        const [analysisOrders, analysisResults, powderOrders, rawMaterialControls] = await Promise.all([
                            getAllOiAnalysisOrdersGrouped(),
                            getAllOiAnalysisResultsGrouped(),
                            getAllOiPowderOrdersGrouped(),
                            getAllRawMaterialControlsGrouped(),
                        ]);
                        setAllAnalysisOrders(analysisOrders);
                        setAllAnalysisResults(analysisResults);
                        setAllPowderOrders(powderOrders);
                        setAllRawMaterialControls(rawMaterialControls);
                    }
                } catch (error) {
                    console.error(error);
                    setAllAnalysisOrders(null);
                    setAllAnalysisResults(null);
                    setAllPowderOrders(null);
                    setAllRawMaterialControls(null);
                }
            };

            fetchData();

        }, 400);

        return () => clearTimeout(timeout);

    }, [key, searchQuery, refreshTick]);

    const handleSearch = React.useCallback((value: string) => {
        setSearchQuery(value);
        setPages({});
    }, []);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchQuery !== undefined) {

                fetchAllReports();
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery, fetchAllReports]);

    const eventDrafts = React.useMemo(() => allReports.filter(r => r.isDraft === true), [allReports]);
    const eventCompleted = React.useMemo(() => allReports.filter(r => r.isDraft === false), [allReports]);
    const powderDrafts = React.useMemo(() => {
        return [...(allPowderOrders?.drafts ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allPowderOrders, comparePowderOrderIds]);

    const powderCompleted = React.useMemo(() => {
        return [...(allPowderOrders?.nonDrafts ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allPowderOrders, comparePowderOrderIds]);
    const powderCancelled = React.useMemo(() => {
        return [...(allPowderOrders?.cancelled ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allPowderOrders, comparePowderOrderIds]);
    const analysisDrafts = React.useMemo(() => {
        return [...(allAnalysisOrders?.drafts ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allAnalysisOrders, comparePowderOrderIds]);
    const analysisReady = React.useMemo(() => {
        return [...(allAnalysisOrders?.readyForReview ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allAnalysisOrders, comparePowderOrderIds]);
    const analysisFailed = React.useMemo(() => {
        return [...(allAnalysisOrders?.failedReview ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allAnalysisOrders, comparePowderOrderIds]);
    const analysisResultNotStarted = React.useMemo(() => {
        return [...(allAnalysisResults?.notStarted ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.analysisOrderId, b.analysisOrderId));
    }, [allAnalysisResults, comparePowderOrderIds]);
    const analysisResultInProgress = React.useMemo(() => {
        return [...(allAnalysisResults?.inProgress ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.analysisOrderId, b.analysisOrderId));
    }, [allAnalysisResults, comparePowderOrderIds]);
    const analysisResultCompleted = React.useMemo(() => {
        return [...(allAnalysisResults?.completed ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.analysisOrderId, b.analysisOrderId));
    }, [allAnalysisResults, comparePowderOrderIds]);
    const rawMaterialDrafts = React.useMemo(() => {
        return [...(allRawMaterialControls?.drafts ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allRawMaterialControls, comparePowderOrderIds]);

    const rawMaterialCompleted = React.useMemo(() => {
        return [...(allRawMaterialControls?.nonDrafts ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allRawMaterialControls, comparePowderOrderIds]);

    const rawMaterialCancelled = React.useMemo(() => {
        return [...(allRawMaterialControls?.cancelled ?? [])]
            .sort((a, b) => comparePowderOrderIds(a.orderId, b.orderId));
    }, [allRawMaterialControls, comparePowderOrderIds]);

    const orderItems = (orders: { uuid?: string; orderId?: string }[]): PaginatedTreeItem[] => orders.map((order, idx) => ({
        key: order.uuid || `analysis-order-${idx}`,
        label: order.orderId,
        path: `/Oi/analysisOrder/${order.uuid}`,
        activePaths: [`/Oi/analysisOrderEdit/${order.uuid}`],
    }));

    const eventItems = (reports: OiReport[]): PaginatedTreeItem[] => reports.map((report, idx) => ({
        key: report.guid || `event-${idx}`,
        label: report.testName,
        path: `/Oi/eventReport/${report.guid}`,
        activePaths: [`/Oi/editEventReport/${report.guid}`],
    }));

    const uuidItems = (orders: { uuid?: string; orderId?: string }[], keyPrefix: string, basePath: string): PaginatedTreeItem[] => orders.map((order, idx) => ({
        key: order.uuid || `${keyPrefix}-${idx}`,
        label: order.orderId,
        path: `${basePath}/${order.uuid}`,
        activePaths: [`${basePath}Edit/${order.uuid}`],
    }));

    const resultItems = (results: { uuid?: string; analysisOrderUuid?: string; analysisOrderId?: string }[]): PaginatedTreeItem[] => results.map((result, idx) => ({
        key: result.uuid || result.analysisOrderUuid || `analysis-result-${idx}`,
        label: result.analysisOrderId,
        // A result that has not been started has no row yet, so it is opened through its order.
        path: result.uuid ? `/Oi/analysisResult/${result.uuid}` : `/Oi/analysisResult/order/${result.analysisOrderUuid}`,
        activePaths: result.analysisOrderUuid ? [`/Oi/analysisResult/order/${result.analysisOrderUuid}`] : [],
    }));

    return (
        <div className="">
            <Tree
                className="text-primary-dark dark:text-primary-light"
                openItems={openItems}
                onOpenChange={(_, data) => setOpenItems(new Set(Array.from(data.openItems, item => String(item))))}
            >
                <div ref={newButtonContainerRef} className="bg-oi-button-blue  mb-4 mt-4">
                    <Menu
                        open={isNewMenuOpen}
                        onOpenChange={(_, data) => {
                            setIsNewMenuOpen(data.open);
                            if (data.open) {
                                const width = newButtonRef.current?.getBoundingClientRect().width;
                                if (width) setNewMenuWidth(width);

                                const bgSource = newButtonContainerRef.current ?? newButtonRef.current;
                                if (bgSource) {
                                    const computedBg = window.getComputedStyle(bgSource).backgroundColor;
                                    if (computedBg && computedBg !== 'rgba(0, 0, 0, 0)' && computedBg !== 'transparent') {
                                        setNewMenuBg(computedBg);
                                    }
                                }
                            }
                        }}
                    >
                        <MenuTrigger disableButtonEnhancement>

                            <button
                                style={{ borderRadius: 0, border: 'none' }}
                                ref={newButtonRef}
                                data-tour="oi-create-button"
                                className="border-radius-0 flex w-full items-center justify-between font-bold text-white px-2 p-2"
                            >
                                <span>New</span>
                                <ChevronDown24Filled />
                            </button>
                        </MenuTrigger>
                        <MenuPopover
                            style={{
                                borderRadius: 0,
                                ...(newMenuWidth ? { width: newMenuWidth } : null),
                                ...(newMenuBg ? {
                                    backgroundColor: newMenuBg,
                                    ['--colorNeutralBackground1' as any]: newMenuBg,
                                    ['--colorNeutralBackground1Hover' as any]: newMenuBg,
                                    ['--colorNeutralBackground1Pressed' as any]: newMenuBg,
                                } : null),
                            }}
                        >
                            <MenuList
                                style={newMenuBg ? {
                                    borderRadius: 0,
                                    backgroundColor: newMenuBg,
                                    ['--colorNeutralBackground1' as any]: newMenuBg,
                                    ['--colorNeutralBackground1Hover' as any]: newMenuBg,
                                    ['--colorNeutralBackground1Pressed' as any]: newMenuBg,
                                } : undefined}
                            >
                                <MenuItem style={{ color: 'white' }} onClick={() => navigate("/Oi/eventReport/new")}>New event report</MenuItem>
                                <MenuItem style={{ color: 'white' }} onClick={() => navigate("/Oi/powderOrder/new")}>New powder order</MenuItem>
                                <MenuItem style={{ color: 'white' }} onClick={() => navigate("/Oi/analysisOrder/new")}>New analysis order</MenuItem>
                                <MenuItem style={{ color: 'white' }} onClick={() => navigate("/Oi/rawMaterialControl/new")}>New raw material control</MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>

                <SearchBox

                    placeholder="Search"
                    value={searchQuery}
                    onChange={(_, data) => handleSearch(data.value)}
                    className="[&_*]:text-[#e5e7eb]  [&:focus-within::after]:bg-red-500
    [&:focus-within::after]:h-[2px]"
                />
                <div className="my-2"></div>
                <Divider />



                <TreeItem value={"event-reports"} itemType={"branch"}>
                    <TreeItemLayout>
                        <Text>
                            Event reports
                        </Text>
                    </TreeItemLayout>
                    <Tree>
                        <PaginatedTreeGroup
                            value="event-drafts"
                            title="Drafts"
                            items={eventItems(eventDrafts)}
                            page={pages["event-drafts"] ?? 1}
                            onPageChange={page => setPage("event-drafts", page)}
                            colorClass="tree-item"
                            emptyText="No drafts found"
                            searchActive={searchActive}
                            onNavigate={navigate}
                        />
                        <PaginatedTreeGroup
                            value="event-completed"
                            title="Completed"
                            items={eventItems(eventCompleted)}
                            page={pages["event-completed"] ?? 1}
                            onPageChange={page => setPage("event-completed", page)}
                            colorClass="text-green-600"
                            emptyText="No completed event reports found"
                            searchActive={searchActive}
                            onNavigate={navigate}
                        />
                    </Tree>
                </TreeItem>

                <TreeItem value={"powder-orders"} itemType={"branch"}>
                    <TreeItemLayout>
                        <Text>
                            Powder orders
                        </Text>
                    </TreeItemLayout>
                    <Tree>
                        <PaginatedTreeGroup
                            value="powder-drafts"
                            title="Drafts"
                            items={uuidItems(powderDrafts, "powder-draft", "/Oi/powderOrder")}
                            page={pages["powder-drafts"] ?? 1}
                            onPageChange={page => setPage("powder-drafts", page)}
                            colorClass="text-orange-600"
                            emptyText="No drafts found"
                            searchActive={searchActive}
                            onNavigate={navigate}
                        />
                        <PaginatedTreeGroup
                            value="powder-completed"
                            title="Completed"
                            items={uuidItems(powderCompleted, "powder-completed", "/Oi/powderOrder")}
                            page={pages["powder-completed"] ?? 1}
                            onPageChange={page => setPage("powder-completed", page)}
                            colorClass="text-green-600"
                            emptyText="No completed powder orders found"
                            searchActive={searchActive}
                            onNavigate={navigate}
                        />
                        <PaginatedTreeGroup
                            value="powder-cancelled"
                            title="Cancelled"
                            items={uuidItems(powderCancelled, "powder-cancelled", "/Oi/powderOrder")}
                            page={pages["powder-cancelled"] ?? 1}
                            onPageChange={page => setPage("powder-cancelled", page)}
                            colorClass="text-green-600"
                            emptyText="No cancelled powder orders found"
                            searchActive={searchActive}
                            onNavigate={navigate}
                        />
                    </Tree>
                </TreeItem>

                <TreeItem value={"raw-material-controls"} itemType={"branch"}>
                    <TreeItemLayout>
                        <Text>
                            Raw material control
                        </Text>
                    </TreeItemLayout>
                    <Tree>
                        <PaginatedTreeGroup
                            value="raw-material-drafts"
                            title="Drafts"
                            items={uuidItems(rawMaterialDrafts, "raw-material-draft", "/Oi/rawMaterialControl")}
                            page={pages["raw-material-drafts"] ?? 1}
                            onPageChange={page => setPage("raw-material-drafts", page)}
                            colorClass="text-orange-600"
                            emptyText="No drafts found"
                            searchActive={searchActive}
                            onNavigate={navigate}
                        />
                        <PaginatedTreeGroup
                            value="raw-material-completed"
                            title="Completed"
                            items={uuidItems(rawMaterialCompleted, "raw-material-completed", "/Oi/rawMaterialControl")}
                            page={pages["raw-material-completed"] ?? 1}
                            onPageChange={page => setPage("raw-material-completed", page)}
                            colorClass="text-green-600"
                            emptyText="No completed raw material controls found"
                            searchActive={searchActive}
                            onNavigate={navigate}
                        />
                        <PaginatedTreeGroup
                            value="raw-material-cancelled"
                            title="Cancelled"
                            items={uuidItems(rawMaterialCancelled, "raw-material-cancelled", "/Oi/rawMaterialControl")}
                            page={pages["raw-material-cancelled"] ?? 1}
                            onPageChange={page => setPage("raw-material-cancelled", page)}
                            colorClass="text-green-600"
                            emptyText="No cancelled raw material controls found"
                            searchActive={searchActive}
                            onNavigate={navigate}
                        />
                    </Tree>
                </TreeItem>

                <TreeItem value={"analysis-orders"} itemType={"branch"}>
                    <TreeItemLayout>
                        <Text>
                            Analysis orders
                        </Text>
                    </TreeItemLayout>
                    <Tree>
                        <TreeItem value={"analysis-assignment"} itemType={"branch"}>
                            <TreeItemLayout>
                                <Text>Assignment</Text>
                            </TreeItemLayout>
                            <Tree>
                                <PaginatedTreeGroup
                                    value="analysis-drafts"
                                    title="Drafts"
                                    items={orderItems(analysisDrafts)}
                                    page={pages["analysis-drafts"] ?? 1}
                                    onPageChange={page => setPage("analysis-drafts", page)}
                                    colorClass="text-orange-600"
                                    emptyText="No drafts found"
                                    searchActive={searchActive}
                                    onNavigate={navigate}
                                />
                                <PaginatedTreeGroup
                                    value="analysis-ready"
                                    title="Ready for review"
                                    items={orderItems(analysisReady)}
                                    page={pages["analysis-ready"] ?? 1}
                                    onPageChange={page => setPage("analysis-ready", page)}
                                    colorClass="text-amber-600"
                                    emptyText="No review-ready analysis orders found"
                                    searchActive={searchActive}
                                    onNavigate={navigate}
                                />
                                <PaginatedTreeGroup
                                    value="analysis-failed"
                                    title="Failed"
                                    items={orderItems(analysisFailed)}
                                    page={pages["analysis-failed"] ?? 1}
                                    onPageChange={page => setPage("analysis-failed", page)}
                                    colorClass="text-red-600"
                                    emptyText="No failed analysis orders found"
                                    searchActive={searchActive}
                                    onNavigate={navigate}
                                />
                            </Tree>
                        </TreeItem>
                        <TreeItem value={"analysis-results"} itemType={"branch"}>
                            <TreeItemLayout>
                                <Text>Result</Text>
                            </TreeItemLayout>
                            <Tree>
                                <PaginatedTreeGroup
                                    value="analysis-result-not-started"
                                    title="Not started"
                                    items={resultItems(analysisResultNotStarted)}
                                    page={pages["analysis-result-not-started"] ?? 1}
                                    onPageChange={page => setPage("analysis-result-not-started", page)}
                                    colorClass="text-orange-600"
                                    emptyText="No analysis results waiting to start"
                                    searchActive={searchActive}
                                    onNavigate={navigate}
                                />
                                <PaginatedTreeGroup
                                    value="analysis-result-in-progress"
                                    title="In progress"
                                    items={resultItems(analysisResultInProgress)}
                                    page={pages["analysis-result-in-progress"] ?? 1}
                                    onPageChange={page => setPage("analysis-result-in-progress", page)}
                                    colorClass="text-amber-600"
                                    emptyText="No in-progress analysis results found"
                                    searchActive={searchActive}
                                    onNavigate={navigate}
                                />
                                <PaginatedTreeGroup
                                    value="analysis-result-completed"
                                    title="Completed"
                                    items={resultItems(analysisResultCompleted)}
                                    page={pages["analysis-result-completed"] ?? 1}
                                    onPageChange={page => setPage("analysis-result-completed", page)}
                                    colorClass="text-green-600"
                                    emptyText="No completed analysis results found"
                                    searchActive={searchActive}
                                    onNavigate={navigate}
                                />
                            </Tree>
                        </TreeItem>
                    </Tree>
                </TreeItem>
            </Tree>
        </div>
    );
}



